import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ShieldCheckIcon } from 'lucide-react';
import { Form, FormAnswer } from '../../types';
import { AnswerMap, visibleQuestions } from '../../utils/conditional';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { QuestionField } from './QuestionField';
export interface DynamicFormResult {
  answers: FormAnswer[];
  contact: Record<string, string>;
}
interface DynamicFormProps {
  form: Form;
  onSubmit?: (result: DynamicFormResult) => Promise<void>;
  /** En vista previa el formulario no envía datos. */
  preview?: boolean;
  /** Permite ocultar la barra de progreso desde el bloque de la landing. */
  showProgress?: boolean;
  /** Permite ocultar el aviso de privacidad desde el bloque de la landing. */
  hidePrivacyNotice?: boolean;
  className?: string;
}
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Prefijos internacionales para el campo de teléfono. */
const COUNTRY_CODES = [{
  value: '+34',
  label: '🇪🇸 España +34'
}, {
  value: '+52',
  label: '🇲🇽 México +52'
}, {
  value: '+54',
  label: '🇦🇷 Argentina +54'
}, {
  value: '+56',
  label: '🇨🇱 Chile +56'
}, {
  value: '+57',
  label: '🇨🇴 Colombia +57'
}, {
  value: '+51',
  label: '🇵🇪 Perú +51'
}, {
  value: '+1',
  label: '🇺🇸 EE. UU. +1'
}, {
  value: '+351',
  label: '🇵🇹 Portugal +351'
}, {
  value: '+33',
  label: '🇫🇷 Francia +33'
}, {
  value: '+39',
  label: '🇮🇹 Italia +39'
}, {
  value: '+44',
  label: '🇬🇧 Reino Unido +44'
}, {
  value: '+49',
  label: '🇩🇪 Alemania +49'
}];

/**
 * Formulario dinámico paso a paso.
 * Las preguntas NO están escritas dentro del componente: llegan siempre
 * desde la configuración del formulario (`form.questions`).
 */
export function DynamicForm({
  form,
  onSubmit,
  preview = false,
  showProgress = true,
  hidePrivacyNotice = false,
  className = ''
}: DynamicFormProps) {
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [contact, setContact] = useState<Record<string, string>>({});
  const [dialCode, setDialCode] = useState('+34');
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewDone, setPreviewDone] = useState(false);
  const questions = useMemo(() => visibleQuestions(form.questions, answers), [form.questions, answers]);

  // El bloque de contacto puede ir al principio (recomendado) o al final.
  const contactFirst = form.contactPosition !== 'final';
  // Un formulario sin datos de contacto no debe mostrar un paso vacío.
  const hasContact = form.contactFields.length > 0;
  const totalSteps = questions.length + (hasContact ? 1 : 0);
  const contactStepIndex = hasContact ? contactFirst ? 0 : questions.length : -1;
  const isContactStep = step === contactStepIndex;
  const current = hasContact && contactFirst ? questions[step - 1] : questions[step];
  const progress = Math.round((step + 1) / totalSteps * 100);
  function setAnswer(id: string, value: string | string[]) {
    setAnswers(prev => ({
      ...prev,
      [id]: value
    }));
    setErrors(prev => ({
      ...prev,
      [id]: ''
    }));
  }
  function validateQuestion(): boolean {
    if (!current || !current.required) return true;
    const value = answers[current.id];
    const empty = value === undefined || (Array.isArray(value) ? value.length === 0 : String(value).trim() === '');
    if (empty) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Esta pregunta es obligatoria.'
      }));
      return false;
    }
    if (current.type === 'email' && !EMAIL_RE.test(String(value))) {
      setErrors(prev => ({
        ...prev,
        [current.id]: 'Introduce un email válido.'
      }));
      return false;
    }
    return true;
  }
  function validateContact(): boolean {
    const next: Record<string, string> = {};
    form.contactFields.forEach(field => {
      const value = (contact[field.key] || '').trim();
      if (field.required && !value) next[field.key] = 'Campo obligatorio.';else if (field.type === 'email' && value && !EMAIL_RE.test(value)) next[field.key] = 'Introduce un email válido.';else if (field.type === 'telefono' && value && value.replace(/\D/g, '').length < 6) next[field.key] = 'Introduce un teléfono válido.';
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  }
  function goNext() {
    if (isContactStep ? !validateContact() : !validateQuestion()) return;
    setDirection(1);
    setStep(s => Math.min(s + 1, totalSteps - 1));
  }
  function goBack() {
    setDirection(-1);
    setStep(s => Math.max(0, s - 1));
  }

  /** Avance con Enter en preguntas de una sola línea. */
  function handleKeyDown(event: React.KeyboardEvent<HTMLFormElement>) {
    const target = event.target as HTMLElement;
    if (event.key !== 'Enter' || target.tagName === 'TEXTAREA') return;
    if (!isLastStep) {
      event.preventDefault();
      goNext();
    }
  }
  const isLastStep = step === totalSteps - 1;

  /** Aviso de privacidad editable y ocultable desde el editor. */
  const notice = form.privacyNotice ?? {
    enabled: true,
    text: 'Al enviar este formulario aceptas que utilicemos tus datos para responder a tu solicitud.'
  };
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;
    if (isContactStep && !validateContact()) return;
    if (!isContactStep && !validateQuestion()) return;
    if (preview || !onSubmit) {
      // En vista previa no se crea ningún lead, pero se confirma que la
      // validación ha pasado y el formulario está listo para publicarse.
      setPreviewDone(true);
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload: FormAnswer[] = questions.map(question => ({
        questionId: question.id,
        label: question.label,
        value: answers[question.id] ?? ''
      }));
      const phoneKey = form.contactFields.find(f => f.type === 'telefono')?.key;
      const contactData = phoneKey ? {
        ...contact,
        [phoneKey]: `${dialCode} ${contact[phoneKey] ?? ''}`.trim()
      } : contact;
      await onSubmit({
        answers: payload,
        contact: contactData
      });
    } catch (err) {
      // Se muestra la causa real para poder diagnosticar el fallo.
      setSubmitError(err instanceof Error && err.message ? err.message : 'No hemos podido enviar tu solicitud. Inténtalo de nuevo en unos segundos.');
      setSubmitting(false);
    }
  }
  if (form.questions.length === 0 && form.contactFields.length === 0) {
    return <div className="rounded-2xl border border-dashed border-border bg-surface p-10 text-center text-muted-foreground" data-id="element-332" data-mp="3a8gg8oke0vgd">
        Este formulario todavía no tiene preguntas.
      </div>;
  }
  return <div className={`mx-auto w-full max-w-[820px] overflow-hidden rounded-2xl border border-border bg-background shadow-soft sm:rounded-3xl ${className}`} data-id="element-333" data-mp="2rm0ixz26z1vy">
      <div className="px-5 py-7 sm:px-10 sm:py-10" data-id="element-334" data-mp="30lnbrwguuzde">
        {/* Progreso */}
        <div className={showProgress ? 'mb-8 sm:mb-10' : 'hidden'} data-id="element-335" data-mp="2vlhqqz53cb4a">
          <div className="mb-2.5 flex items-center justify-between text-sm" data-id="element-336" data-mp="3aj1r1sr1li44">
            <span className="font-medium text-muted-foreground" data-id="element-337" data-mp="3agxdej3cd4qn">
              Paso {step + 1} de {totalSteps}
            </span>
            <span className="tabular-nums text-muted-foreground" data-id="element-338" data-mp="3jb3pjlhjwobr">{progress}%</span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-label="Progreso del formulario" data-id="element-339" data-mp="3fa09oyzi6saj">
            <motion.div className="h-full rounded-full bg-primary" initial={false} animate={{
            width: `${Math.max(progress, 4)}%`
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} data-id="element-340" data-mp="3oag7psxh9780" />
          </div>
        </div>

        <form onSubmit={handleSubmit} onKeyDown={handleKeyDown} noValidate data-id="element-341" data-mp="13h9zt67r6s39">
          <AnimatePresence mode="wait" initial={false} data-id="element-342" data-mp="18jhdq1c58i88">
            <motion.div key={isContactStep ? 'contacto' : current?.id} initial={{
            opacity: 0,
            x: direction * 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: direction * -20
          }} transition={{
            duration: 0.22,
            ease: 'easeOut'
          }} data-id="element-343" data-mp="1dh0tx6sej52t">
              {isContactStep ? <div className="space-y-6" data-id="element-344" data-mp="uiernol5f7xb">
                  <div className="space-y-2" data-id="element-345" data-mp="uj6of2qhfbp6">
                    <h3 className="text-[22px] font-semibold leading-tight tracking-tight text-navy sm:text-[28px]" data-id="element-346" data-mp="3t8ewl2fc84a4">
                      {form.contactTitle || 'Empecemos por tu información de contacto'}
                    </h3>
                    <p className="text-sm text-muted-foreground sm:text-base" data-id="element-347" data-mp="3qo8tsy2gmjv5">
                      Solo te llevará un minuto.
                    </p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-348" data-mp="3gpszync1swhy">
                    {form.contactFields.map(field => field.type === 'telefono' ? <div key={field.key} className="sm:col-span-2" data-id="element-349" data-mp="3brhs2i5bcw2d">
                          <div className="grid grid-cols-[minmax(0,150px)_minmax(0,1fr)] gap-3" data-id="element-350" data-mp="37p4n3vye526n">
                            <Select id="contact-dial" label="País" options={COUNTRY_CODES} value={dialCode} onChange={e => setDialCode(e.target.value)} data-id="element-351" data-mp="32yejoo9iz9el" />
                            <Input id={`contact-${field.key}`} label={`${field.label}${field.required ? ' *' : ''}`} type="tel" inputMode="tel" placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                      ...prev,
                      [field.key]: e.target.value
                    }))} data-id="element-352" data-mp="2xztgcfyz89lx" />
                          </div>
                        </div> : <div key={field.key} className={field.key === 'email' || field.key === 'website' || field.key === 'company' ? 'sm:col-span-2' : ''} data-id="element-353" data-mp="1akj36saczt9v">
                          <Input id={`contact-${field.key}`} label={`${field.label}${field.required ? ' *' : ''}`} type={field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'} inputMode={field.type === 'email' ? 'email' : undefined} placeholder={field.placeholder} value={contact[field.key] || ''} error={errors[field.key]} onChange={e => setContact(prev => ({
                    ...prev,
                    [field.key]: e.target.value
                  }))} data-id="element-354" data-mp="1kcglsk3p5nim" />
                        </div>)}
                  </div>
                </div> : current ? <QuestionField question={current} value={answers[current.id]} error={errors[current.id]} onChange={value => {
              setAnswer(current.id, value);
              // Avance automático en selección única.
              if (current.type === 'seleccion_unica' && !isLastStep) {
                window.setTimeout(() => {
                  setDirection(1);
                  setStep(s => Math.min(s + 1, totalSteps - 1));
                }, 260);
              }
            }} data-id="element-355" data-mp="wop410ze0i9y" /> : null}
            </motion.div>
          </AnimatePresence>

          {submitError && <p role="alert" className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-id="element-356" data-mp="2amz68r1mgj1d">
              {submitError}
            </p>}

          {previewDone && <p role="status" className="mt-6 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700" data-id="element-357" data-mp="33b4o961ajtxi">
              Vista previa: el formulario es válido. En la página publicada este
              envío crearía un contacto en tu CRM.
            </p>}

          <div className="mt-9 flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-358" data-mp="2tip7ud5n7deq">
            <Button type="button" variant="ghost" onClick={goBack} disabled={step === 0 || submitting} className="min-h-[48px] w-full sm:w-auto" leftIcon={<ArrowLeftIcon className="h-4 w-4" data-id="element-360" data-mp="3vwju7e495j4x" />} data-id="element-359" data-mp="3m1mdh0mx07e4">
              Anterior
            </Button>
            {isLastStep ? <Button type="submit" size="lg" loading={submitting} disabled={submitting} className="ava-glow min-h-[52px] w-full sm:w-auto" data-id="element-361" data-mp="3qw5j1n7ctt2o">
                {preview ? 'Vista previa' : form.submitLabel}
              </Button> : <Button type="button" size="lg" onClick={goNext} className="min-h-[52px] w-full sm:w-auto" rightIcon={<ArrowRightIcon className="h-4 w-4" data-id="element-363" data-mp="387qv58sc2v63" />} data-id="element-362" data-mp="3cygzqjsxqtzq">
                Siguiente
              </Button>}
          </div>
        </form>

        {/* Aviso informativo: nunca bloquea el envío. */}
        {notice.enabled && !hidePrivacyNotice && <p className="mt-6 flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center text-xs leading-relaxed text-muted-foreground" data-id="element-364" data-mp="2z7vguv3hek1u">
            <ShieldCheckIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-365" data-mp="2ua2d08pni24q" />
            <span data-id="element-366" data-mp="2p9ocrcuajwh9">{notice.text}</span>
            {notice.linkUrl && <a href={notice.linkUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline-offset-2 hover:underline" data-id="element-367" data-mp="14rdl0vuqcy3y">
                {notice.linkLabel || 'Política de privacidad'}
              </a>}
          </p>}
      </div>
    </div>;
}